import React from 'react';

// Performance monitoring utilities for lazy loading
export const performanceMonitor = {
  // Track component load times
  trackComponentLoad: (componentName, startTime = performance.now()) => {
    return () => {
      const endTime = performance.now();
      const loadTime = endTime - startTime;
      
      if (process.env.NODE_ENV === 'development') {
        console.log(`🚀 ${componentName} loaded in ${loadTime.toFixed(2)}ms`);
      }
      
      // Optional: Send to analytics
      if (typeof gtag !== 'undefined') {
        gtag('event', 'component_load_time', {
          component_name: componentName,
          load_time: Math.round(loadTime),
          custom_parameter: 'lazy_loading'
        });
      }
    };
  },

  // Track bundle size impact
  trackBundleSize: () => {
    if (process.env.NODE_ENV === 'development' && 'connection' in navigator) {
      const connection = navigator.connection;
      console.log('📊 Network Info:', {
        effectiveType: connection.effectiveType,
        downlink: connection.downlink,
        rtt: connection.rtt,
        saveData: connection.saveData
      });
    }
  },

  // Track lazy loading success
  trackLazyLoadSuccess: (componentName) => {
    if (process.env.NODE_ENV === 'development') {
      console.log(`✅ Lazy loaded: ${componentName}`);
    }
  },

  // Track lazy loading errors
  trackLazyLoadError: (componentName, error) => {
    console.error(`❌ Failed to lazy load ${componentName}:`, error);
    
    // Optional: Send to error tracking
    if (typeof gtag !== 'undefined') {
      gtag('event', 'lazy_load_error', {
        component_name: componentName,
        error_message: error.message
      });
    }
  }
};

// HOC to wrap lazy components with performance tracking
export const withPerformanceTracking = (LazyComponent, componentName) => {
  return React.forwardRef((props, ref) => {
    React.useEffect(() => {
      const trackLoad = performanceMonitor.trackComponentLoad(componentName);
      performanceMonitor.trackLazyLoadSuccess(componentName);
      return trackLoad;
    }, []);

    return React.createElement(LazyComponent, { ...props, ref });
  });
};

// Error boundary for lazy loading
export class LazyLoadErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    performanceMonitor.trackLazyLoadError(this.props.componentName || 'Unknown', error);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-neutral-50 flex items-center justify-center">
          <div className="text-center">
            <div className="text-6xl mb-4">😕</div>
            <h2 className="text-2xl font-bold text-charcoal-800 mb-2">
              Oops! Something went wrong
            </h2>
            <p className="text-charcoal-600 mb-4">
              We're having trouble loading this page. Please try refreshing.
            </p>
            <button
              onClick={() => window.location.reload()}
              className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
            >
              Refresh Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
} 